/**
 * Ersatzteilefinder — entry point (esbuild IIFE bundle).
 *
 * Replaces the legacy product-attribute-description finder: instead of
 * hydrating scraper-baked HTML, it renders the redesigned three-axis finder
 * from the plugin-assembled JSON island (datte rows → CompatContentProvider
 * → Smarty-var seam; §7 interactive-shop-foundation, D4/O4).
 *
 * Boot contract (emitted by the theme's {ersatzteil_finder} seam in
 * src/productdetails/tabs.tpl — the ONE mount point):
 *   <div class="datte-ersatzteil-finder" data-labels='{json label bag}'>
 *     <script type="application/json" class="datte-fits-for-payload">
 *       {version:1, value:{kind:"fits-for-models", …}}
 *     </script>
 *   </div>
 *
 * Deep links: reads/writes the legacy `model` / `category` / `spectrum`
 * URL params (history.replaceState — no history spam); CTA links carry
 * `?category&spectrum&article` to the category page (unchanged contract).
 */

import { parseFitsForPayload } from './data/wire';
import { FinderSelection, type FinderUrlParams } from './domain/selection';
import { FinderView, labelsFromJson } from './view/finder-view';

const initialized = new WeakSet<Element>();

function readUrlParams(): FinderUrlParams {
  const params = new URLSearchParams(window.location.search);
  return {
    model: params.get('model'),
    category: params.get('category'),
    spectrum: params.get('spectrum'),
  };
}

function writeUrlParams(selection: FinderSelection): void {
  const state = selection.toParams();
  const url = new URL(window.location.href);
  if (state.model) url.searchParams.set('model', state.model);
  url.searchParams.set('category', state.category ?? '');
  if (state.spectrum) {
    url.searchParams.set('spectrum', state.spectrum);
  } else {
    url.searchParams.delete('spectrum');
  }
  history.replaceState(null, '', url);
}

function bootFinder(container: HTMLElement): void {
  if (initialized.has(container)) return;

  const payloadScript = container.querySelector<HTMLScriptElement>(
    'script.datte-fits-for-payload'
  );
  if (!payloadScript?.textContent) return;

  const payload = parseFitsForPayload(payloadScript.textContent);
  if (!payload) return; // legacy/unknown format — leave the container alone

  const labels = labelsFromJson(container.getAttribute('data-labels'));
  const selection = new FinderSelection(payload);
  selection.restoreFromParams(readUrlParams());

  const view = new FinderView(container, labels, {
    onSelectModel(index) {
      selection.selectModel(index);
      view.render(selection);
      writeUrlParams(selection);
    },
    onSelectSpectrum(index) {
      selection.selectSpectrum(index);
      view.render(selection);
      writeUrlParams(selection);
    },
    onSelectDrawing(index) {
      selection.selectDrawing(index);
      view.render(selection);
      writeUrlParams(selection);
    },
    onSearch() {
      view.renderModelFilter(selection);
    },
  });

  view.render(selection);
  initialized.add(container);
}

function bootAll(): void {
  document
    .querySelectorAll<HTMLElement>('.datte-ersatzteil-finder')
    .forEach((container) => {
      try {
        bootFinder(container);
      } catch (error) {
        console.error('[ersatzteil-finder] boot failed', error);
      }
    });
}

/** Containers can arrive after load (tab content, cached-page injection). */
function watchForDynamicContent(): void {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of Array.from(mutation.addedNodes)) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        const element = node as HTMLElement;
        if (
          element.matches?.('.datte-ersatzteil-finder') ||
          element.querySelector?.('.datte-ersatzteil-finder')
        ) {
          bootAll();
          return;
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    bootAll();
    watchForDynamicContent();
  });
} else {
  bootAll();
  watchForDynamicContent();
}
